import React from "react";
import Link from "next/link";
import Image from "next/image";
import { ExternalLink, ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { LinkAnchor } from "@/app/components/ui/link-anchor";
import {
  formatStatus,
  getStatusColorClass,
} from "@/app/utils/helpers/format/status";
import { formatDate } from "@/app/utils/helpers/format/date";
import { getFallbackValue } from "@/app/utils/helpers/backend/backend-table/fallback-utils";
import type {
  BackendTableRowT,
  BackendTableRouteConfigT,
} from "@/app/types/backend-table";
import type { StatusT } from "@/app/types/status";

/**
 * Cell renderer function type
 */
export type CellRendererT = (
  value: unknown,
  row: BackendTableRowT,
  routeConfig?: BackendTableRouteConfigT
) => React.ReactNode;

function ImagePlaceholder({
  className,
  iconClassName = "size-4",
}: {
  className: string;
  iconClassName?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-center bg-[var(--c-surface-sunken)]",
        className
      )}
    >
      <ImageIcon
        className={cn(iconClassName, "text-[var(--c-muted)] opacity-40")}
      />
    </div>
  );
}

/**
 * Standard cell renderers for common data types
 */
export const CellRenderers = {
  /**
   * Default text renderer
   */
  text: (value: unknown): React.ReactNode => (
    <span
      className="block max-w-full truncate text-sm text-gray-600"
      title={typeof value === "string" ? value : undefined}
    >
      {getFallbackValue(value)}
    </span>
  ),

  /**
   * Date renderer with formatting
   */
  date: (value: unknown): React.ReactNode => {
    const fallbackValue = getFallbackValue(value);
    return (
      <span className="text-sm text-gray-600">
        {fallbackValue !== "-" ? formatDate(fallbackValue) : "-"}
      </span>
    );
  },

  /**
   * Status renderer with color coding
   */
  status: (value: unknown): React.ReactNode => {
    const fallbackValue = getFallbackValue(value);
    return (
      <span
        className={cn(
          "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
          getStatusColorClass(fallbackValue as StatusT)
        )}
      >
        {formatStatus(fallbackValue as StatusT)}
      </span>
    );
  },

  /**
   * Image renderer for logos and previews
   */
  image: (value: unknown, row: BackendTableRowT): React.ReactNode => {
    const fallbackValue = getFallbackValue(value);
    const altText = (row.previewAlt as string) || "Image";

    if (fallbackValue !== "-") {
      return (
        <Image
          src={fallbackValue}
          alt={altText}
          width={64}
          height={64}
          className="size-16 rounded-md object-cover"
        />
      );
    }

    return <ImagePlaceholder className="size-16 rounded-md" />;
  },

  /**
   * Small image renderer for brand logos
   */
  smallImage: (value: unknown): React.ReactNode => {
    const fallbackValue = getFallbackValue(value);

    if (fallbackValue !== "-") {
      return (
        <Image
          src={fallbackValue}
          alt="Logo"
          width={32}
          height={32}
          className="size-8 max-h-6 rounded object-contain"
        />
      );
    }

    return (
      <ImagePlaceholder
        className="size-8 max-h-6 rounded"
        iconClassName="size-3.5"
      />
    );
  },

  /**
   * Profile image renderer for contact persons (circular with object-cover)
   */
  profileImage: (value: unknown): React.ReactNode => {
    const fallbackValue = getFallbackValue(value);

    if (fallbackValue !== "-") {
      return (
        <Image
          src={fallbackValue}
          alt="Profilbild"
          width={40}
          height={40}
          className="size-10 shrink-0 rounded-full object-cover"
        />
      );
    }

    return <ImagePlaceholder className="size-10 shrink-0 rounded-full" />;
  },

  /**
   * Thumbnail image renderer for offers (16:9 aspect ratio, smaller size)
   */
  thumbnailImage: (value: unknown): React.ReactNode => {
    const fallbackValue = getFallbackValue(value);

    if (fallbackValue !== "-") {
      return (
        <Image
          src={fallbackValue}
          alt="Thumbnail"
          width={80}
          height={45}
          className="h-8 w-14 rounded object-cover"
        />
      );
    }

    return <ImagePlaceholder className="h-8 w-14 rounded" />;
  },

  /**
   * External link renderer with icon
   */
  externalLink: (value: unknown): React.ReactNode => {
    const fallbackValue = getFallbackValue(value);

    if (fallbackValue !== "-") {
      return (
        <LinkAnchor
          href={fallbackValue}
          target="_blank"
          rel="noopener noreferrer"
          variant="transparent"
          underline="hover"
          className="inline-flex items-center gap-1"
          asNextLink={false}
        >
          Link
          <ExternalLink size={14} />
        </LinkAnchor>
      );
    }

    return <span className="text-sm text-gray-600">-</span>;
  },

  /**
   * Internal link renderer for detail pages
   */
  internalLink: (
    value: unknown,
    row: BackendTableRowT,
    routeConfig?: BackendTableRouteConfigT
  ): React.ReactNode => {
    const fallbackValue = getFallbackValue(value);

    if (routeConfig) {
      return (
        <Link
          href={`${routeConfig.rootPath ?? "/dashboard"}/${routeConfig.basePath}/${row[routeConfig.slugField || "slug"]}`}
          className="text-sm font-semibold text-[#18191A] hover:text-gray-800"
        >
          {fallbackValue}
        </Link>
      );
    }

    return <span className="text-sm text-gray-600">{fallbackValue}</span>;
  },

  /**
   * Campaign action link renderer
   */
  campaignLink: (value: unknown): React.ReactNode => {
    if (
      value &&
      typeof value === "object" &&
      "href" in value &&
      "title" in value
    ) {
      return (
        <Link
          href={value.href as string}
          className="block max-w-full truncate text-sm text-gray-600 underline hover:text-gray-800"
        >
          {value.title as string}
        </Link>
      );
    }

    return (
      <span className="block max-w-full truncate text-sm text-gray-600">-</span>
    );
  },

  /**
   * Contact person link renderer for user list
   * Shows "Ja" with link if contact person exists, "Nein" otherwise
   */
  contactPersonLink: (value: unknown): React.ReactNode => {
    if (
      value &&
      typeof value === "object" &&
      "href" in value &&
      "label" in value
    ) {
      return (
        <Link
          href={value.href as string}
          className="text-sm text-gray-600 underline hover:text-gray-800"
        >
          {value.label as string}
        </Link>
      );
    }

    return <span className="text-sm text-gray-600">Nein</span>;
  },

  /**
   * Lead contact renderer with salutation/name and email stacked.
   */
  leadContact: (
    value: unknown,
    row: BackendTableRowT,
    routeConfig?: BackendTableRouteConfigT
  ): React.ReactNode => {
    const fallbackValue = getFallbackValue(value);
    const salutation =
      typeof row.salutation === "string" && row.salutation !== "-"
        ? row.salutation
        : null;
    const email =
      typeof row.email === "string" && row.email !== "-" ? row.email : null;
    const displayName = [salutation, fallbackValue]
      .filter((part) => part && part !== "-")
      .join(" ");
    const content = (
      <span className="block min-w-0">
        <span className="block truncate text-sm font-semibold text-[#18191A]">
          {displayName || fallbackValue}
        </span>
        {email && (
          <span className="mt-0.5 block truncate text-[13px] font-normal text-gray-500">
            {email}
          </span>
        )}
      </span>
    );

    if (routeConfig) {
      return (
        <Link
          href={`${routeConfig.rootPath ?? "/dashboard"}/${routeConfig.basePath}/${row[routeConfig.slugField || "slug"]}`}
          className="block min-w-0 hover:text-gray-800"
        >
          {content}
        </Link>
      );
    }

    return content;
  },

  /**
   * Primary relation renderer for leads (offer > vehicle > campaign > company)
   */
  primaryRelation: (value: unknown, row: BackendTableRowT): React.ReactNode => {
    const relation = row.primaryRelation as
      | {
          type: "offer" | "campaign" | "company" | "vehicle" | "service";
          id: string;
          title: string | null;
          slug: string | null;
          url?: string | null;
        }
      | null
      | undefined;
    const fallbackSource = row.source as string | null | undefined;

    if (!relation) {
      const displayValue =
        (typeof fallbackSource === "string" && fallbackSource.trim() !== ""
          ? fallbackSource
          : null) || "-";

      return <span className="text-sm text-gray-600">{displayValue}</span>;
    }

    // Use external URL if available (e.g., for gebrauchtwagen vehicles)
    // Otherwise, use dashboard link
    const href = relation.url
      ? relation.url
      : relation.slug
        ? (() => {
            const hrefMap: Record<
              "offer" | "campaign" | "company" | "vehicle" | "service",
              string
            > = {
              offer: "/dashboard/angebote",
              campaign: "/dashboard/aktionen",
              company: "/dashboard/firmen",
              service: "/dashboard/serviceleistungen",
              vehicle: "/gebrauchtwagen", // Fallback, but should use relation.url
            };
            return `${hrefMap[relation.type]}/${relation.slug}`;
          })()
        : null;

    const title = relation.title || relation.slug || "-";

    if (!href) {
      return <span className="text-sm text-gray-600">{title}</span>;
    }

    return (
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-gray-600 underline hover:text-gray-800"
      >
        {title}
      </Link>
    );
  },
};

/**
 * Cell renderer factory function
 */
export const createCellRenderer = (
  type: string,
  routeConfig?: BackendTableRouteConfigT
): CellRendererT => {
  switch (type) {
    case "date":
      return CellRenderers.date;
    case "status":
      return CellRenderers.status;
    case "image":
      return CellRenderers.image;
    case "smallImage":
      return CellRenderers.smallImage;
    case "profileImage":
      return CellRenderers.profileImage;
    case "thumbnailImage":
      return CellRenderers.thumbnailImage;
    case "externalLink":
      return CellRenderers.externalLink;
    case "internalLink":
      return (value, row) =>
        CellRenderers.internalLink(value, row, routeConfig);
    case "campaignLink":
      return CellRenderers.campaignLink;
    case "contactPersonLink":
      return CellRenderers.contactPersonLink;
    case "leadContact":
      return (value, row) => CellRenderers.leadContact(value, row, routeConfig);
    case "primaryRelation":
      return CellRenderers.primaryRelation;
    case "text":
    default:
      return CellRenderers.text;
  }
};
